import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import { EventCreateContent } from "@/features/create-event/components/EventCreateContent";
import { EventDeleteContent } from "@/features/study-group-events/components/EventDeleteContent";
import { EventDetailContent } from "@/features/study-group-events/components/EventDetailContent";
import { EventDuplicateContent } from "@/features/study-group-events/components/EventDuplicateContent";
import { EventEditContent } from "@/features/study-group-events/components/EventEditContent";
import { EventListContent } from "@/features/study-group-events/components/EventListContent";
import type {
  ExtendedModalActions,
  ModalState,
} from "@/features/study-group-events/type";
import { MODAL_CONFIGS, ModalType } from "@/features/study-group-events/type";
import { useEventById } from "@/hooks/useEventById";

interface ModalManagerProps {
  state: ModalState;
  actions: ExtendedModalActions;
  onDataRefresh: () => void;
  showOrganizerInDayList?: boolean;
}

export const EventModalManager: React.FC<ModalManagerProps> = ({
  state,
  actions,
  onDataRefresh,
  showOrganizerInDayList = false,
}) => {
  const { t } = useTranslation();
  const [isCreateFlowCompleted, setIsCreateFlowCompleted] = useState(false);
  const {
    activeModal,
    selectedEvent,
    selectedEventApiData,
    selectedDate,
    selectedEvents,
  } = state;
  const config = activeModal ? MODAL_CONFIGS[activeModal] : null;

  const {
    data: fullEventData,
    isLoading: fullEventLoading,
    error: fullEventError,
  } = useEventById(
    activeModal === ModalType.EVENT_DETAIL && selectedEvent?.id
      ? selectedEvent.id
      : 0,
  );

  useEffect(() => {
    if (
      activeModal !== ModalType.EVENT_CREATE &&
      activeModal !== ModalType.EVENT_DUPLICATE
    ) {
      setIsCreateFlowCompleted(false);
    }
  }, [activeModal]);

  const handleSuccess = (options?: { resetModal?: boolean }) => {
    onDataRefresh();
    setIsCreateFlowCompleted(false);

    if (options?.resetModal) {
      actions.closeModal({ reset: true });
    } else {
      actions.closeModal();
    }
  };

  const handleDeleteSuccess = () => {
    onDataRefresh();
    setIsCreateFlowCompleted(false);
    actions.closeModal({ reset: true });
  };

  const handleModalClose = () => {
    if (isCreateFlowCompleted) {
      handleSuccess();
      return;
    }

    actions.closeModal();
  };

  const renderModalContent = () => {
    switch (activeModal) {
      case ModalType.EVENT_DETAIL:
        if (fullEventLoading) {
          return (
            <div className="flex h-full flex-col p-6">
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-2 size-8 animate-spin rounded-full border-b-2 border-gray-900" />
                  {t("common.loading")}
                </div>
              </div>
            </div>
          );
        }

        if (fullEventError) {
          const errorMessage =
            typeof fullEventError === "string"
              ? fullEventError
              : (fullEventError as { message?: string } | null | undefined)
                  ?.message || t("common.unknownError");
          return (
            <div className="flex h-full flex-col p-6">
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center text-red-500">
                  <p className="mb-2 text-lg">{t("common.error")}</p>
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            </div>
          );
        }

        return fullEventData ? (
          <EventDetailContent
            event={fullEventData}
            onEdit={(event, eventApiData) =>
              actions.openEventEdit(event, eventApiData)
            }
            onDuplicate={(event, eventApiData) =>
              actions.openEventDuplicate(event, eventApiData)
            }
            onDelete={(event, eventApiData, scope) =>
              actions.openEventDelete(event, eventApiData, scope)
            }
            onDataRefresh={onDataRefresh}
            showLocationDetails
            showDuplicateButton={true}
          />
        ) : null;

      case ModalType.EVENT_CREATE:
        return (
          <EventCreateContent
            onSuccess={handleSuccess}
            onCompletionStateChange={setIsCreateFlowCompleted}
            initialDate={selectedDate || undefined}
          />
        );

      case ModalType.EVENT_EDIT:
        return selectedEventApiData ? (
          <EventEditContent
            eventData={selectedEventApiData}
            onSaveSuccess={handleSuccess}
            onCancel={actions.closeModal}
          />
        ) : null;

      case ModalType.EVENT_DUPLICATE:
        return selectedEventApiData ? (
          <EventDuplicateContent
            eventData={selectedEventApiData}
            onSuccess={handleSuccess}
            onCompletionStateChange={setIsCreateFlowCompleted}
          />
        ) : null;

      case ModalType.EVENT_DELETE:
        return selectedEventApiData ? (
          <EventDeleteContent
            eventData={selectedEventApiData}
            onDeleteSuccess={handleDeleteSuccess}
            onCancel={actions.closeModal}
            initialScope={state.selectedDeleteScope ?? undefined}
          />
        ) : null;

      case ModalType.DAY_EVENTS:
        return selectedDate ? (
          <EventListContent
            events={selectedEvents}
            date={selectedDate}
            onEventClick={(event) => actions.openEventDetail(event)}
            showOrganizer={showOrganizerInDayList}
          />
        ) : null;

      default:
        return null;
    }
  };

  if (!activeModal) {
    return null;
  }

  return (
    <BasicModal
      isOpen={!!activeModal}
      onClose={handleModalClose}
      maxWidth={config?.maxWidth || "max-w-lg"}
      maxHeight={config?.maxHeight}
    >
      {renderModalContent()}
    </BasicModal>
  );
};
